import React, { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { z } from 'zod';

import { resetPasswordRequest } from '@/services/auth/authService';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useTranslation } from '@/store/languageStore';
import api from '@/services/api/client';

const inputBase = 'w-full px-3 py-2 pr-10 border rounded-md bg-white dark:bg-white text-gray-900 dark:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500';
const inputBorderOk = 'border-gray-300 dark:border-gray-300';
const inputBorderErr = 'border-red-500';
const labelClass = 'block text-gray-700 dark:text-gray-700 text-sm font-bold mb-2';
const eyeBtnClass = 'absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600 hover:text-gray-800';
const pageClass = 'min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-100';
const cardClass = 'max-w-md w-full bg-white dark:bg-white rounded-lg shadow-md p-8';
const titleClass = 'text-3xl font-bold text-center mb-8 text-gray-800 dark:text-gray-800';

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  usePageTitle(t('resetPassword.title'));
  const emailFormSchema = useMemo(
    () => z.object({
      email: z.string().email(t('copy.admin.invalidEmail')),
    }),
    [t],
  );
  const passwordResetFormSchema = useMemo(
    () => z.object({
      password: z.string().min(8, t('copy.admin.passwordMin')),
      confirmPassword: z.string().min(8, t('copy.admin.passwordMin')),
    }).refine((data) => data.password === data.confirmPassword, {
      message: t('copy.admin.passwordsMismatch'),
      path: ['confirmPassword'],
    }),
    [t],
  );
  type EmailFormData = z.infer<typeof emailFormSchema>;
  type PasswordResetFormData = z.infer<typeof passwordResetFormSchema>;
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (tokenParam) {
      setToken(tokenParam);
    }
  }, [searchParams]);

  const {
    register: registerEmail,
    handleSubmit: handleSubmitEmail,
    formState: { errors: emailErrors },
  } = useForm<EmailFormData>({
    resolver: zodResolver(emailFormSchema),
  });

  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    formState: { errors: passwordErrors },
  } = useForm<PasswordResetFormData>({
    resolver: zodResolver(passwordResetFormSchema),
  });

  const onEmailSubmit = async (data: EmailFormData) => {
    setIsLoading(true);
    try {
      await resetPasswordRequest(data.email);
      setEmailSent(true);
      toast.success(t('copy.admin.resetEmailSentToast'));
    } catch {
      setEmailSent(true);
      toast.success(t('copy.admin.resetEmailSentToast'));
    } finally {
      setIsLoading(false);
    }
  };

  const onPasswordSubmit = async (data: PasswordResetFormData) => {
    if (!token) {
      toast.error(t('copy.admin.resetTokenMissing'));
      return;
    }

    setIsLoading(true);
    try {
      await api.post('/api/auth/reset-password/confirm', {
        token,
        password: data.password,
      });
      toast.success(t('copy.admin.resetSuccess'));
      navigate('/login');
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || t('copy.admin.resetFailed');
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (token) {
    return (
      <div className={pageClass}>
        <div className={cardClass}>
          <h2 className={titleClass}>
            {t('resetPassword.title') }
          </h2>

          <form onSubmit={handleSubmitPassword(onPasswordSubmit)} className="space-y-6">
            <div>
              <label htmlFor="password" className={labelClass}>
                {t('resetPassword.newPasswordLabel') } <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  {...registerPassword('password')}
                  className={`${inputBase} ${passwordErrors.password ? inputBorderErr : inputBorderOk}`}
                  placeholder={t('resetPassword.newPasswordPlaceholder') }
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={eyeBtnClass}
                  disabled={isLoading}
                >
                  {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                </button>
              </div>
              {passwordErrors.password && (
                <p className="mt-1 text-sm text-red-500">{passwordErrors.password.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className={labelClass}>
                {t('resetPassword.confirmPasswordLabel') } <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  {...registerPassword('confirmPassword')}
                  className={`${inputBase} ${passwordErrors.confirmPassword ? inputBorderErr : inputBorderOk}`}
                  placeholder={t('resetPassword.confirmPasswordPlaceholder') }
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className={eyeBtnClass}
                  disabled={isLoading}
                >
                  {showConfirmPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                </button>
              </div>
              {passwordErrors.confirmPassword && (
                <p className="mt-1 text-sm text-red-500">{passwordErrors.confirmPassword.message}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className={`w-full bg-blue-500 text-white font-bold py-2 px-4 rounded-md hover:bg-blue-600 transition duration-200 ${
                isLoading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isLoading
                ? t('resetPassword.resetting') 
                : t('resetPassword.resetButton') }
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-textSecondary dark:text-textSecondary">
            <Link to="/login" className="text-blue-600 hover:text-blue-800 font-semibold">
              {t('resetPassword.backToLogin') }
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (emailSent) {
    return (
      <div className={pageClass}>
        <div className={cardClass}>
          <div className="text-center">
            <div className="mb-4">
              <svg
                className="mx-auto h-12 w-12 text-green-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-800 mb-4">
              {t('resetPassword.emailSentTitle') }
            </h2>
            <p className="text-textSecondary dark:text-textSecondary mb-6">
              {t('resetPassword.emailSentMessage') }
            </p>
            <Link
              to="/login"
              className="inline-block bg-blue-500 text-white font-bold py-2 px-4 rounded-md hover:bg-blue-600 transition duration-200"
            >
              {t('resetPassword.backToLogin') }
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={pageClass}>
      <div className={cardClass}>
        <h2 className={titleClass}>
          {t('resetPassword.title') }
        </h2>

        <p className="text-textSecondary dark:text-textSecondary mb-6 text-center">
          {t('resetPassword.description') }
        </p>

        <form onSubmit={handleSubmitEmail(onEmailSubmit)} className="space-y-6">
          <div>
            <label htmlFor="email" className={labelClass}>
              {t('login.emailLabel') } <span className="text-red-500">*</span>
            </label>
            <input
              id="email"
              type="email"
              {...registerEmail('email')}
              className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-white text-gray-900 dark:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                emailErrors.email ? inputBorderErr : inputBorderOk
              }`}
              placeholder={t('login.emailPlaceholder') }
              disabled={isLoading}
            />
            {emailErrors.email && <p className="mt-1 text-sm text-red-500">{emailErrors.email.message}</p>}
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className={`w-full bg-blue-500 text-white font-bold py-2 px-4 rounded-md hover:bg-blue-600 transition duration-200 ${
              isLoading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isLoading
              ? t('resetPassword.sending') 
              : t('resetPassword.sendButton') }
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-textSecondary dark:text-textSecondary">
          {t('resetPassword.rememberPassword') }{' '}
          <Link to="/login" className="text-blue-600 hover:text-blue-800 font-semibold">
            {t('resetPassword.backToLogin') }
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
